/**
 * The companies a feature is on for.
 *
 * Some flags are decided per company, and the company is only known where it
 * is on screen: a row, a drawer, a form. So the flag serves the whole list once,
 * and the call site checks the company it holds. In Vercel Flags it is a JSON
 * value: `{ "include": "*", "exclude": ["<id>"] }` is everyone but one company,
 * and `{ "include": ["<id>"], "exclude": [] }` is one company.
 */
export type CompanyRollout = {
    include: '*' | string[]
    exclude: string[]
}

/** An excluded company stays out, even when `include` is `*`. */
export const isCompanyIncluded = (rollout: CompanyRollout, companyId: string | undefined): boolean => {
    if (!companyId) return false
    if (rollout.exclude.includes(companyId)) return false

    return rollout.include === '*' || rollout.include.includes(companyId)
}

const isStringList = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every((item) => typeof item === 'string')

/** `undefined` when the value has another shape. A missing `exclude` means nobody is excluded. */
export const decodeCompanyRollout = (value: unknown): CompanyRollout | undefined => {
    if (typeof value !== 'object' || value === null) return undefined

    const include: unknown = Reflect.get(value, 'include')
    const exclude: unknown = Reflect.get(value, 'exclude') ?? []

    if ((include !== '*' && !isStringList(include)) || !isStringList(exclude)) return undefined

    return { include, exclude }
}
